import type { Alignment, Profession } from '@continuum/show-core';
import { screenText } from './screenText.js';
import type { Lang } from './text.js';

export interface ChronicleCardData {
  lang: Lang;
  callsign: string;
  profession: Profession | null;
  alignment: Alignment | null;
  /** Set at docking; there is no winner, only `damage` (2026-09-29). */
  outcome: 'home' | 'crew' | 'agents' | null;
  damage?: number;
  uprising: boolean;
  contributions: string[];
}

const GOLD = '#d9b96a';
const INK = '#f3ead6';
const DIM = '#b9a678';
const MONO = "'IBM Plex Mono', monospace";
const SANS = "'Rajdhani', sans-serif";

export type Ctx = CanvasRenderingContext2D & { letterSpacing?: string };

export function put(ctx: Ctx, text: string, x: number, y: number, font: string, color: string, spacing = 0, align: CanvasTextAlign = 'left') {
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.letterSpacing = `${spacing}px`;
  ctx.fillText(text, x, y);
}

/** Words onto lines no wider than `max`; returns where the next line would start. */
export function wrap(ctx: Ctx, text: string, x: number, y: number, max: number, lineHeight: number, font: string, color: string): number {
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textAlign = 'left';
  ctx.letterSpacing = '0px';
  let line = '';
  for (const word of text.split(' ')) {
    const tried = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(tried).width > max) {
      ctx.fillText(line, x, y);
      y += lineHeight;
      line = word;
    } else {
      line = tried;
    }
  }
  if (line) {
    ctx.fillText(line, x, y);
    y += lineHeight;
  }
  return y;
}

/** The biggest size, down from `size`, at which `text` fits in `max` pixels. */
function fitted(ctx: Ctx, text: string, max: number, size: number, spacing: number): string {
  let px = size;
  ctx.letterSpacing = `${spacing}px`;
  for (; px > 48; px -= 4) {
    ctx.font = `700 ${px}px ${SANS}`;
    if (ctx.measureText(text).width <= max) break;
  }
  return `700 ${px}px ${SANS}`;
}

/**
 * The result card as a picture to keep and share: 1080×1350, drawn on the phone itself and
 * never uploaded anywhere. No names but the call sign, no dates, no years, no brands.
 */
export async function drawChronicleCard(card: ChronicleCardData): Promise<Blob> {
  const s = screenText(card.lang);
  await document.fonts?.ready;
  const W = 1080;
  const H = 1350;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx: Ctx | null = canvas.getContext('2d');
  if (!ctx) throw new Error('this phone cannot draw the card');

  const ground = ctx.createLinearGradient(0, 0, 0, H);
  ground.addColorStop(0, '#17130a');
  ground.addColorStop(1, '#0a0806');
  ctx.fillStyle = ground;
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = 'rgba(217,185,106,.45)';
  ctx.lineWidth = 3;
  ctx.strokeRect(50, 50, W - 100, H - 100);

  put(ctx, s.cardTitle, 100, 150, `500 30px ${MONO}`, GOLD, 6);
  put(ctx, s.cardSub, 100, 196, `400 26px ${MONO}`, DIM, 4);

  put(ctx, card.callsign, 100, 360, fitted(ctx, card.callsign, W - 200, 132, 8), INK, 8);
  if (card.profession) put(ctx, card.profession, 100, 420, `500 32px ${MONO}`, DIM, 5);

  ctx.fillStyle = 'rgba(217,185,106,.35)';
  ctx.fillRect(100, 470, W - 200, 2);

  let y = 545;
  for (const line of card.contributions) {
    y = wrap(ctx, `· ${line}`, 100, y, W - 200, 46, `400 32px ${MONO}`, '#dcd0b4') + 10;
  }

  const role = card.alignment === 'PARADOX AGENT' ? s.roleSaboteur : card.alignment === 'PRESERVER' ? s.roleCrew : '—';
  y += 30;
  put(ctx, s.role, 100, y, `500 26px ${MONO}`, '#9c8a63', 5);
  put(ctx, role, W - 100, y, `700 48px ${SANS}`, card.alignment === 'PARADOX AGENT' ? '#ff5f86' : '#4fd8c2', 6, 'right');

  if (card.uprising) {
    y += 80;
    y = wrap(ctx, s.uprisingLine, 100, y, W - 200, 40, `500 28px ${MONO}`, '#ff9fb3');
  }

  // No winner (2026-09-29): the damage the ship came home with.
  if (card.outcome) {
    const top = Math.max(y + 40, 980);
    ctx.strokeStyle = GOLD;
    ctx.lineWidth = 3;
    ctx.strokeRect(100, top, W - 200, 170);
    const headline = s.cameHome(card.damage ?? 0);
    put(ctx, headline, W / 2, top + 98, fitted(ctx, headline, W - 260, 52, 4), GOLD, 4, 'center');
  }

  put(ctx, s.cardFooter, W / 2, H - 100, `400 24px ${MONO}`, '#9c8a63', 4, 'center');

  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('no image'))), 'image/png'),
  );
}
